/**
 * Super-admin editor for the Landing page Hero + Problem section copy.
 *
 * Every field mirrors the shape used by `useLandingHero`. Local form state is
 * flushed to the server via `PUT /admin/landing-hero`. A "Reset to defaults"
 * button restores the hard-coded copy shipped with the app.
 */
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sparkles, Save, Loader2, RotateCcw, Plus, Trash2, Type as TypeIcon, Eye, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { LandingHeroConfig, DEFAULT_HERO } from "@/hooks/useLandingHero";

export const LandingHeroCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";
  const [form, setForm] = useState<LandingHeroConfig>(DEFAULT_HERO);

  const q = useQuery<LandingHeroConfig>({
    queryKey: ["admin-landing-hero"],
    queryFn: async () => (await api.get("/admin/landing-hero")).data,
  });

  useEffect(() => {
    if (q.data) setForm(q.data);
  }, [q.data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/landing-hero", form)).data,
    onSuccess: () => {
      toast.success("Landing hero copy saved.");
      qc.invalidateQueries({ queryKey: ["admin-landing-hero"] });
      // Bust the public hook cache so the /landing preview updates immediately.
      qc.invalidateQueries({ queryKey: ["landing-hero"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post("/admin/landing-hero/reset")).data,
    onSuccess: (d: LandingHeroConfig) => {
      toast.success("Landing hero restored to defaults.");
      setForm(d);
      qc.invalidateQueries({ queryKey: ["admin-landing-hero"] });
      qc.invalidateQueries({ queryKey: ["landing-hero"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Reset failed"),
  });

  const upd = <K extends keyof LandingHeroConfig>(k: K, v: LandingHeroConfig[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const updBullet = (i: number, text: string) => {
    const next = [...form.bullets];
    next[i] = { text };
    upd("bullets", next);
  };
  const addBullet = () => {
    if (form.bullets.length >= 6) return;
    upd("bullets", [...form.bullets, { text: "New bullet" }]);
  };
  const removeBullet = (i: number) => {
    upd("bullets", form.bullets.filter((_, idx) => idx !== i));
  };

  const updStat = (i: number, key: "value" | "label", v: string) => {
    const next = form.problem_stats.map((s, idx) => idx === i ? { ...s, [key]: v } : s);
    upd("problem_stats", next);
  };

  return (
    <div className="sg-card p-6" data-testid="landing-hero-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">
            Landing Hero &amp; Problem copy
          </div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5 leading-snug">
            Edit the homepage headline, sub-copy, CTAs, checkmark bullets and
            the three &ldquo;drowning in comments&rdquo; stat pills. Wrap words
            in <code className="font-mono">**double asterisks**</code> inside
            sub-copy to bold them.
          </div>
        </div>
      </div>

      <div className="grid xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
        <div className="min-w-0">
      {/* --- Hero section --- */}
      <Section title="Hero" subtitle="First thing every visitor sees.">
        <Field label="Trial badge" testId="hero-badge-text">
          <Input
            data-testid="hero-badge-input"
            value={form.badge_text}
            disabled={readonly}
            onChange={(e) => upd("badge_text", e.target.value)}
            maxLength={200}
          />
        </Field>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Headline · line 1 (before accent)">
            <Textarea
              data-testid="hero-headline-prefix"
              rows={2}
              value={form.headline_prefix}
              disabled={readonly}
              onChange={(e) => upd("headline_prefix", e.target.value)}
              maxLength={240}
            />
          </Field>
          <Field label="Headline · accent (orange)">
            <Input
              data-testid="hero-headline-accent"
              value={form.headline_accent}
              disabled={readonly}
              onChange={(e) => upd("headline_accent", e.target.value)}
              maxLength={120}
            />
          </Field>
          <Field label="Headline · suffix (after accent)">
            <Textarea
              data-testid="hero-headline-suffix"
              rows={2}
              value={form.headline_suffix}
              disabled={readonly}
              onChange={(e) => upd("headline_suffix", e.target.value)}
              maxLength={240}
            />
          </Field>
        </div>
        <Field label="Sub-copy (supports **bold**)">
          <Textarea
            data-testid="hero-subcopy"
            rows={4}
            value={form.subcopy}
            disabled={readonly}
            onChange={(e) => upd("subcopy", e.target.value)}
            maxLength={800}
          />
        </Field>

        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Primary CTA · label">
            <Input
              data-testid="hero-primary-cta-label"
              value={form.primary_cta_label}
              disabled={readonly}
              onChange={(e) => upd("primary_cta_label", e.target.value)}
              maxLength={48}
            />
          </Field>
          <Field label="Primary CTA · href">
            <Input
              data-testid="hero-primary-cta-href"
              value={form.primary_cta_href}
              disabled={readonly}
              onChange={(e) => upd("primary_cta_href", e.target.value)}
              placeholder="/signup or https://…"
              maxLength={512}
            />
          </Field>
          <Field label="Secondary CTA · label">
            <Input
              data-testid="hero-secondary-cta-label"
              value={form.secondary_cta_label}
              disabled={readonly}
              onChange={(e) => upd("secondary_cta_label", e.target.value)}
              maxLength={48}
            />
          </Field>
          <Field label="Secondary CTA · href">
            <Input
              data-testid="hero-secondary-cta-href"
              value={form.secondary_cta_href}
              disabled={readonly}
              onChange={(e) => upd("secondary_cta_href", e.target.value)}
              placeholder="/pricing"
              maxLength={512}
            />
          </Field>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs uppercase tracking-wider">Checkmark bullets · up to 6</Label>
            {!readonly && form.bullets.length < 6 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="hero-bullet-add"
                onClick={addBullet}
                className="h-7 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {form.bullets.map((b, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`hero-bullet-row-${i}`}>
                <TypeIcon className="h-3.5 w-3.5 text-[color:var(--sg-muted)] shrink-0" />
                <Input
                  data-testid={`hero-bullet-input-${i}`}
                  value={b.text}
                  disabled={readonly}
                  onChange={(e) => updBullet(i, e.target.value)}
                  maxLength={120}
                  className="flex-1"
                />
                {!readonly && form.bullets.length > 1 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid={`hero-bullet-remove-${i}`}
                    onClick={() => removeBullet(i)}
                    className="h-8 border-red-200 text-red-700 hover:bg-red-50 shrink-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* --- Problem section --- */}
      <Section title="Problem section" subtitle="Dark strip below the Networks marquee.">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Headline · line 1 (before accent)">
            <Textarea
              data-testid="problem-headline-prefix"
              rows={2}
              value={form.problem_headline_prefix}
              disabled={readonly}
              onChange={(e) => upd("problem_headline_prefix", e.target.value)}
              maxLength={240}
            />
          </Field>
          <Field label="Headline · accent (dimmed)">
            <Textarea
              data-testid="problem-headline-accent"
              rows={2}
              value={form.problem_headline_accent}
              disabled={readonly}
              onChange={(e) => upd("problem_headline_accent", e.target.value)}
              maxLength={240}
            />
          </Field>
        </div>
        <Field label="Sub-copy">
          <Textarea
            data-testid="problem-subcopy"
            rows={3}
            value={form.problem_subcopy}
            disabled={readonly}
            onChange={(e) => upd("problem_subcopy", e.target.value)}
            maxLength={800}
          />
        </Field>
        <div>
          <Label className="text-xs uppercase tracking-wider mb-2 block">Stat pills · exactly 3</Label>
          <div className="grid md:grid-cols-3 gap-3">
            {form.problem_stats.map((s, i) => (
              <div
                key={i}
                className="rounded-lg border border-[color:var(--sg-border)] p-3 space-y-2"
                data-testid={`problem-stat-row-${i}`}
              >
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Value</Label>
                  <Input
                    data-testid={`problem-stat-value-${i}`}
                    value={s.value}
                    disabled={readonly}
                    onChange={(e) => updStat(i, "value", e.target.value)}
                    maxLength={24}
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Label</Label>
                  <Textarea
                    data-testid={`problem-stat-label-${i}`}
                    rows={2}
                    value={s.label}
                    disabled={readonly}
                    onChange={(e) => updStat(i, "label", e.target.value)}
                    maxLength={120}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* --- Actions --- */}
      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--sg-border)] pt-4">
        <div className="text-[11px] text-[color:var(--sg-muted)]">
          Changes go live instantly across every visitor. Sub-copy accepts
          <span className="font-mono"> **bold** </span> tokens.
        </div>
        {!readonly && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="landing-hero-reset-btn"
              disabled={reset.isPending || save.isPending}
              onClick={() => {
                if (window.confirm("Restore the shipped-with-app default hero copy? Unsaved edits will be lost."))
                  reset.mutate();
              }}
              className="h-9"
            >
              {reset.isPending
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
              Reset to defaults
            </Button>
            <Button
              type="button"
              size="sm"
              data-testid="landing-hero-save-btn"
              disabled={save.isPending || reset.isPending}
              onClick={() => save.mutate()}
              className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white h-9"
            >
              {save.isPending
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <Save className="h-3.5 w-3.5 mr-1" />}
              Save changes
            </Button>
          </div>
        )}
      </div>
        </div>
        {/* Live preview panel — sticky on wide desktop screens */}
        <div className="xl:sticky xl:top-4 self-start" data-testid="landing-hero-preview">
          <HeroPreview form={form} />
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; subtitle: string; children: React.ReactNode }> = ({
  title, subtitle, children,
}) => (
  <div className="mt-4 space-y-3 rounded-xl border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 p-4">
    <div>
      <div className="text-sm font-semibold text-[color:var(--sg-fg)]">{title}</div>
      <div className="text-[11px] text-[color:var(--sg-muted)]">{subtitle}</div>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <Label className="text-xs uppercase tracking-wider mb-1 block">{label}</Label>
    {children}
  </div>
);

// ---------------------------------------------------------------
// HeroPreview — a compact, live-rendered snapshot of the current
// form state, styled to loosely resemble the Landing hero. Updates
// in real-time as admin types, giving instant WYSIWYG feedback.
// ---------------------------------------------------------------
const renderBold = (s: string) => {
  // Convert **bold** tokens to <b> spans. Simple, forgiving split.
  const parts = s.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <b key={i} className="text-slate-900">{part}</b>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
};

const HeroPreview: React.FC<{ form: LandingHeroConfig }> = ({ form }) => {
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] overflow-hidden bg-white">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/50">
        <Eye className="h-3.5 w-3.5 text-[color:var(--sg-muted)]" />
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] font-semibold">
          Live preview · updates as you type
        </div>
      </div>
      <div className="relative p-5 bg-[#FDFBF7]" style={{ colorScheme: "light" }}>
        {/* ambient glow */}
        <div
          aria-hidden
          className="absolute left-6 top-6 h-24 w-40 rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(60% 60% at 50% 50%, rgba(249,115,22,0.14) 0%, transparent 70%)",
            filter: "blur(20px)",
          }}
        />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] text-slate-600 mb-3">
            <Sparkles className="h-3 w-3 text-orange-500" strokeWidth={2} />
            <span className="line-clamp-1">{form.badge_text || "Trial badge"}</span>
          </div>
          <div className="font-serif-display font-bold text-slate-900 leading-[1.1] tracking-tight text-lg sm:text-xl">
            {form.headline_prefix?.trim() || <span className="text-slate-400">Headline prefix…</span>}
            {form.headline_prefix?.includes("\n") ? <br /> : " "}
            <span className="text-orange-500">
              {form.headline_accent?.trim() || <span className="text-slate-400">Accent</span>}
            </span>
            {" "}
            {form.headline_suffix?.trim() || ""}
          </div>
          <p className="mt-2.5 text-[11px] leading-relaxed text-slate-600 line-clamp-4">
            {form.subcopy ? renderBold(form.subcopy) : <span className="text-slate-400 italic">Sub-copy…</span>}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <div className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold px-2.5 py-1">
              {form.primary_cta_label?.trim() || "Primary CTA"}
            </div>
            <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 text-slate-700 text-[10px] font-semibold px-2.5 py-1">
              {form.secondary_cta_label?.trim() || "Secondary"}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-2.5 gap-y-1 text-[10px] text-slate-500">
            {(form.bullets || []).slice(0, 4).map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1">
                <Check className="h-2.5 w-2.5 text-emerald-500" strokeWidth={2.5} />
                <span className="line-clamp-1">{b.text || "bullet"}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Problem strip */}
      <div className="relative p-5 border-t border-[color:var(--sg-border)] bg-[#0A1128] text-[#F4F1EA]">
        <div className="font-serif-display font-bold leading-tight text-sm">
          {form.problem_headline_prefix?.trim() || <span className="text-slate-400">Problem prefix…</span>}
          {form.problem_headline_prefix?.includes("\n") ? <br /> : " "}
          <span className="text-white/60 italic">
            {form.problem_headline_accent?.trim() || "…dimmed accent"}
          </span>
        </div>
        <p className="mt-2 text-[10px] leading-relaxed text-white/60 line-clamp-3">
          {form.problem_subcopy || <span className="italic">Sub-copy…</span>}
        </p>
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {form.problem_stats?.slice(0, 3).map((s, i) => (
            <div key={i} className="rounded-md bg-white/5 border border-white/10 px-1.5 py-1.5 text-center">
              <div className="font-serif-display font-bold text-white text-[13px] leading-none tabular-nums">
                {s.value || "—"}
              </div>
              <div className="text-[8px] text-white/50 mt-0.5 leading-tight line-clamp-2">
                {s.label || "label"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LandingHeroCard;
